import * as React from 'react';
import { useId } from '../../utils/hooks/useId.js';
import { Bem, addClass } from '../../utils/css.js';
import { useMergedRefs } from '../../utils/component-util.js';
import { Key, Size } from '../../constants.js';
import { NavigationContext, ConditionalContext } from './NavigationContext.js';
import { useFloating, autoUpdate, useDismiss, useInteractions, FloatingPortal, FloatingFocusManager } from '@floating-ui/react';
import { Icon } from '../Icon/Icon.js';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';

const bem = Bem.create("vai-navigation-submenu");
const NavigationSubMenu = (props) => {
  const navigation = React.useContext(NavigationContext);
  const {
    dataTa,
    children,
    className,
    menuKey,
    htmlId,
    htmlTitle,
    title,
    focusFirstChildOnOpen = true,
    selectedKey = navigation.selectedKey,
    openSubmenuKeys = navigation.openSubmenuKeys,
    onSubmenuToggle = navigation.onSubmenuToggle,
    onSelectItem = navigation.onSelectItem,
    icon,
    expandIcon,
    _internalRef,
    ...rest
  } = props;
  const id = useId(htmlId);
  const ref = React.useRef(null);
  const mergedRefs = useMergedRefs(ref, _internalRef);
  const childMenuKeys = React.useMemo(() => React.Children.map(children, (child) => {
    return child?.props?.menuKey?.toString();
  }), [children]);
  const isChildSelected = React.useMemo(() => {
    return childMenuKeys?.some((menuKey2) => menuKey2 === selectedKey);
  }, [selectedKey, childMenuKeys]);
  const isSelected = React.useMemo(() => {
    return menuKey === selectedKey || isChildSelected;
  }, [selectedKey, menuKey, isChildSelected]);
  const isExpanded = React.useMemo(() => {
    return openSubmenuKeys?.includes(menuKey?.toString());
  }, [openSubmenuKeys, menuKey]);
  const firstChildRef = React.useRef(null);
  const focusFirstChild = React.useCallback(() => {
    if (!focusFirstChildOnOpen) {
      return;
    }
    const firstChild = firstChildRef.current?.firstElementChild;
    if (firstChild instanceof HTMLElement) {
      setTimeout(() => firstChild.focus(), 0);
    }
  }, [focusFirstChildOnOpen, firstChildRef]);
  const toggleOpen = React.useCallback((domEvent) => {
    onSubmenuToggle?.({
      menuKey,
      domEvent
    });
  }, [onSubmenuToggle, menuKey]);
  const handleKeyDown = React.useCallback((event) => {
    if (event.target !== ref.current) {
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    toggleOpen(event);
    if (!isExpanded) {
      requestAnimationFrame(() => focusFirstChild());
    }
  }, [isExpanded, focusFirstChild, toggleOpen]);
  const isFloating = navigation.collapsed && navigation.floatingSubMenu;
  const isFloatingVisible = isFloating && isExpanded;
  const {
    refs,
    strategy,
    x,
    y,
    context: floatingContext
  } = useFloating({
    open: isFloatingVisible,
    placement: "right-start",
    onOpenChange: (open, event) => toggleOpen(event),
    whileElementsMounted: autoUpdate
  });
  const dismiss = useDismiss(floatingContext);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const navigableItemRefs = React.useRef(new Set());
  const addItemRefs = React.useCallback((el) => {
    if (!el || navigableItemRefs.current.has(el)) {
      return;
    }
    const role = el.getAttribute("role");
    const link = el.querySelector("a");
    if (role === "menuitem" && link) {
      navigableItemRefs.current.add(link);
    }
  }, [navigableItemRefs]);
  const onKeyDown = React.useCallback((event) => {
    if (![Key.ArrowUp, Key.ArrowDown, Key.Home, Key.End, Key.Escape].includes(event.key)) {
      return;
    }
    event.stopPropagation();
    const navigableItems = Array.from(navigableItemRefs.current);
    const currentIndex = navigableItems.findIndex((el) => el === document.activeElement);
    const firstIndex = 0;
    const lastIndex = navigableItems.length - 1;
    switch (event.key) {
      case Key.ArrowUp:
        if (currentIndex > firstIndex) {
          navigableItems[currentIndex - 1].focus();
        }
        break;
      case Key.ArrowDown:
        if (currentIndex < lastIndex) {
          navigableItems[currentIndex + 1].focus();
        }
        break;
      case Key.Home:
        navigableItems[firstIndex].focus();
        break;
      case Key.End:
        navigableItems[lastIndex].focus();
        break;
      case Key.Escape:
        ref.current?.focus();
        toggleOpen(event);
        break;
    }
  }, [navigableItemRefs, toggleOpen]);
  const submenuItems = /* @__PURE__ */ React.createElement("ul", {
    className: addClass("vai-navigation-menu", "vai-navigation-menu__sub", !isExpanded && "vai-navigation-menu--hidden"),
    onKeyDown
  }, React.Children.map(children, (child, idx) => {
    if (idx === 0) {
      return React.cloneElement(child, {
        ref: firstChildRef,
        _internalRef: addItemRefs
      });
    }
    return React.cloneElement(child, {
      _internalRef: addItemRefs
    });
  }));
  return /* @__PURE__ */ React.createElement(ConditionalContext, {
    navigation,
    navigationProps: {
      selectedKey,
      openSubmenuKeys,
      onSubmenuToggle,
      onSelectItem
    }
  }, /* @__PURE__ */ React.createElement("li", {
    className: addClass(bem.block(isExpanded && "open", isSelected && "selected"), "vai-navigation-context", className),
    role: "menu",
    id: id.toString(),
    tabIndex: 0,
    "data-ta": dataTa,
    onKeyDown: handleKeyDown,
    ref: mergedRefs,
    title: htmlTitle,
    ...rest
  }, title && /* @__PURE__ */ React.createElement("div", {
    className: addClass(bem.element("title")(), "vai-navigation-item"),
    role: "menuitem",
    "aria-expanded": isExpanded,
    onClick: toggleOpen,
    ref: isFloating ? refs.setReference : void 0,
    ...isFloating ? getReferenceProps() : {}
  }, icon && /* @__PURE__ */ React.createElement("span", {
    className: "vai-navigation-item__icon"
  }, icon), React.isValidElement(title) ? title : /* @__PURE__ */ React.createElement("span", null, title), /* @__PURE__ */ React.createElement("div", {
    className: bem.element("icon")()
  }, expandIcon ?? /* @__PURE__ */ React.createElement(Icon, {
    name: isExpanded ? VaiIcon.TriangleDown : VaiIcon.TriangleRight,
    size: Size.XS
  }))), !isFloating && submenuItems, isFloating && /* @__PURE__ */ React.createElement(FloatingPortal, null, isFloatingVisible && /* @__PURE__ */ React.createElement(FloatingFocusManager, {
    context: floatingContext,
    modal: false,
    initialFocus: -1
  }, /* @__PURE__ */ React.createElement("div", {
    className: "vai-navigation-menu__portal vai-navigation-context",
    ref: refs.setFloating,
    style: { position: strategy, top: y ?? 0, left: x ?? 0 },
    ...getFloatingProps()
  }, submenuItems)))));
};

export { NavigationSubMenu };
